import { ModalHeader } from '@starsoft/common/components';
import {
  CommonModalsKey,
  DepositStep,
  WithdrawStep,
} from '@starsoft/common/enums';
import { useTranslation } from '../../../../../hooks';
import { useCurrentModal } from '../../../../../hooks/modals/useCurrentModal';
import { TransactionOptions } from '../../../props';
import { TransactionsModalsLayoutHeaderProps } from './props';

/**
 * TransactionsModalsLayoutHeader Component
 * This component is responsible for rendering the header of transaction modals.
 * It dynamically sets the title and handles the back button functionality based on the transaction step.
 *
 * @template T - A generic type extending TransactionOptions.
 * @param {TransactionsModalsLayoutHeaderProps<T>} props - The properties for the component.
 * @param {() => void} props.handleBackButton - Callback function to handle the back button action.
 * @param {WithdrawStep | DepositStep} props.transactionStep - The current step in the transaction process.
 * @param {() => void} props.onClose - Callback function to handle the close action.
 *
 * @returns {JSX.Element} The rendered ModalHeader component with appropriate title and actions.
 *
 * @example
 * // Usage example:
 * <TransactionsModalsLayoutHeader
 *   handleBackButton={() => function('Back button clicked')}
 *   transactionStep={WithdrawStep.Form}
 *   onClose={() => function('Modal closed')}
 * />
 */
export default function TransactionsModalsLayoutHeader<
  T extends TransactionOptions,
>({
  handleBackButton,
  transactionStep,
  onClose,
  version,
}: TransactionsModalsLayoutHeaderProps<T>) {
  const { t } = useTranslation('common');
  const modal: string | undefined = useCurrentModal(CommonModalsKey);
  const isDefaultStep: boolean = [WithdrawStep.Form, DepositStep.Form].includes(
    transactionStep,
  );
  const isReponseStep: boolean = [
    WithdrawStep.Response,
    DepositStep.Response,
  ].includes(transactionStep);

  return (
    <ModalHeader
      title={
        isDefaultStep
          ? modal == CommonModalsKey.Deposit
            ? t('tab_deposit')
            : t('tab_withdraw')
          : t(
              `transaction_step_${transactionStep}${isReponseStep ? `_${modal}` : ''}`,
            )
      }
      onClose={onClose}
      version={version}
      withBackButton={!isDefaultStep && !isReponseStep}
      handleGoBack={handleBackButton}
      smallPadding
    />
  );
}
